import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Network, Globe, Smartphone, Wifi, Users as UsersIcon, FileText, CheckCircle, ArrowRight } from 'lucide-react';

const PenetrationTesting = () => {
  const { t, i18n } = useTranslation();
  const testingTypes = [
    {
      icon: <Network className="w-10 h-10" />,
      title: t('pentest.types.network'),
      description: t('pentest.types.networkDesc'),
    },
    {
      icon: <Globe className="w-10 h-10" />,
      title: t('pentest.types.web'),
      description: t('pentest.types.webDesc'),
    },
    {
      icon: <Smartphone className="w-10 h-10" />,
      title: t('pentest.types.mobile'),
      description: t('pentest.types.mobileDesc'),
    },
    {
      icon: <Wifi className="w-10 h-10" />,
      title: t('pentest.types.wireless'),
      description: t('pentest.types.wirelessDesc'),
    },
    {
      icon: <UsersIcon className="w-10 h-10" />,
      title: t('pentest.types.social'),
      description: t('pentest.types.socialDesc'),
    },
  ];

  const methodologies = [
    {
      name: 'OWASP',
      description: t('pentest.methodology.owasp'),
    },
    {
      name: 'OSSTMM',
      description: t('pentest.methodology.osstmm'),
    },
    {
      name: 'PTES',
      description: t('pentest.methodology.ptes'),
    },
    {
      name: 'NIST',
      description: t('pentest.methodology.nist'),
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: t('pentest.process.step1'),
      description: t('pentest.process.step1Desc'),
    },
    {
      number: '02',
      title: t('pentest.process.step2'),
      description: t('pentest.process.step2Desc'),
    },
    {
      number: '03',
      title: t('pentest.process.step3'),
      description: t('pentest.process.step3Desc'),
    },
    {
      number: '04',
      title: t('pentest.process.step4'),
      description: t('pentest.process.step4Desc'),
    },
    {
      number: '05',
      title: t('pentest.process.step5'),
      description: t('pentest.process.step5Desc'),
    },
    {
      number: '06',
      title: t('pentest.process.step6'),
      description: t('pentest.process.step6Desc'),
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="pentest-page">
      <Helmet>
        <title>{i18n.language === 'tr' ? 'Penetrasyon Testi Hizmetleri | SULH' : 'Penetration Testing Services | SULH'}</title>
        <meta name="description" content={i18n.language === 'tr' ? 'Profesyonel penetrasyon testi hizmetleri. Network, web, mobil uygulama sızma testleri. OWASP, OSSTMM metodolojileri.' : 'Professional penetration testing services. Network, web, mobile application security testing.'} />
        <link rel="canonical" href="https://www.sulh.tech/penetration-testing" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20" data-testid="pentest-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="pentest-title">
            {t('pentest.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-8" data-testid="pentest-subtitle">
            {t('pentest.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Types of Testing */}
      <section className="section-padding" data-testid="testing-types-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center" data-testid="types-title">
            {t('pentest.types.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testingTypes.map((type, index) => (
              <div key={index} className="glass-card p-8" data-testid={`testing-type-${index}`}>
                <div className="text-blue-400 mb-4">{type.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{type.title}</h3>
                <p className="text-gray-400">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodologies */}
      <section className="section-padding bg-slate-900/50" data-testid="methodologies-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center" data-testid="methodologies-title">
            {t('pentest.methodologies.title')}
          </h2>
          <p className="text-gray-300 text-center max-w-3xl mx-auto mb-12">
            {t('pentest.methodologies.subtitle')}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodologies.map((method, index) => (
              <div key={index} className="glass-card p-6 text-center" data-testid={`methodology-${index}`}>
                <FileText className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{method.name}</h3>
                <p className="text-gray-400 text-sm">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center" data-testid="process-title">
            {t('pentest.process.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="glass-card p-8" data-testid={`process-step-${index}`}>
                <div className="text-5xl font-bold text-blue-400/20 mb-4">{step.number}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-slate-900/50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="benefits-title">
                {t('pentest.benefits.title')}
              </h2>
              <p className="text-gray-300 mb-6">
                {t('pentest.benefits.subtitle')}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">{t('pentest.benefits.vuln')}</span>
                    <p className="text-gray-400 text-sm mt-1">{t('pentest.benefits.vulnDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">{t('pentest.benefits.comply')}</span>
                    <p className="text-gray-400 text-sm mt-1">{t('pentest.benefits.complyDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">{t('pentest.benefits.validate')}</span>
                    <p className="text-gray-400 text-sm mt-1">{t('pentest.benefits.validateDesc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">{t('pentest.benefits.prioritize')}</span>
                    <p className="text-gray-400 text-sm mt-1">{t('pentest.benefits.prioritizeDesc')}</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="glass-card p-8">
              <h3 className="text-2xl font-semibold text-white mb-6">{t('pentest.reporting.title')}</h3>
              <p className="text-gray-300 mb-6">
                {t('pentest.reporting.subtitle')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item1')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item2')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item3')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item4')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item5')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <span>{t('pentest.reporting.item6')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form CTA */}
      <section className="section-padding" data-testid="pentest-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="pentest-cta-title">
            {t('pentest.cta.title')}
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            {t('pentest.cta.subtitle')}
          </p>
          <Link to="/contact">
            <button className="btn-primary text-lg flex items-center gap-2 mx-auto" data-testid="pentest-cta-button">
              {t('pentest.cta.button')}
              <ArrowRight size={20} />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PenetrationTesting;
